// File: src/Math/Grade8/Grade8MathLesson.jsx
import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import "./Grade8MathLesson.css";

import WritingPad   from "../MathTools/WritingPad";
import Calculator   from "../MathTools/Calculator";
import PlottingPad  from "../MathTools/PlottingPad";

import defaultProfile from "../../UserProfile/EmptyProfile.jpg";
import { chapterPages }   from "./Grade8chapterPages";
import { generateQuizQuestion } from "./Grade8QuizGenerator";

// ---- Firebase ----
import { auth, db } from "../../firebase";
import {
  doc,
  onSnapshot,
  runTransaction,
  serverTimestamp
} from "firebase/firestore";

/*──────────────────────────────────────────────────────────*/
/*  1.  Constants                                           */
/*──────────────────────────────────────────────────────────*/
const COURSE_ID        = "math_g8";
const FINAL_COUNT      = 50;
const PASS_CHAPTER     = 6;   // /10   (≥60 %)
const PASS_FINAL_PCT   = 80;  // %

const chapterTopicMap = {
  1: "ratioPercent",
  2: "fractionOps",
  3: "linearRelations",
  4: "surfaceVolume",
  5: "statistics",
  6: null
};

/*──────────────────────────────────────────────────────────*/
/*  2.  Component                                           */
/*──────────────────────────────────────────────────────────*/
export default function Grade8MathLesson({ onAwardTokens }) {
  /* 2-A. Navigation */
  const [chapter, setChapter] = useState(1);
  const [pageIdx, setPageIdx] = useState(0);

  /* 2-B. Chapter-quiz */
  const [showChQuiz, setShowChQuiz] = useState(false);
  const [chQuiz,     setChQuiz]     = useState([]);
  const [chIdx,      setChIdx]      = useState(0);
  const [chScore,    setChScore]    = useState(0);
  const [chDone,     setChDone]     = useState(false);

  /* 2-C. Final-quiz */
  const [showFinal, setShowFinal] = useState(false);
  const [finalQs,   setFinalQs]   = useState([]);
  const [fIdx,      setFIdx]      = useState(0);
  const [fScore,    setFScore]    = useState(0);
  const [fDone,     setFDone]     = useState(false);

  /* 2-D. UI toggles */
  const [showCalc,    setShowCalc]    = useState(false);
  const [showPad,     setShowPad]     = useState(false);
  const [showPlotter, setShowPlotter] = useState(false);

  /* 2-E. User / progress coming from Firestore */
  const [user, setUser]                       = useState(null);
  const [allDone, setAllDone]                 = useState(false);
  const [hasCompletedBefore, setHasCompleted] = useState(false);

  /* listen to auth + user doc */
  useEffect(() => {
    const unsubAuth = auth.onAuthStateChanged((u) => {
      if (!u) { setUser(null); return; }

      const ref = doc(db, "users", u.uid);
      const unsubDoc = onSnapshot(ref, (snap) => {
        if (!snap.exists()) return;
        const data = snap.data();
        setUser(data);

        const cp = data.courseProgress?.[COURSE_ID];
        if (cp?.completed) {
          setHasCompleted(true);
          setAllDone(true);
        }
      });
      return unsubDoc;
    });

    return unsubAuth;
  }, []);

  /*────────────────────────────────────────────────────────*/
  /* 3. Derived lesson data                                */
  /*────────────────────────────────────────────────────────*/
  const pages          = chapterPages[chapter];
  const currentPage    = pages?.[pageIdx] ?? {};
  const isLastPage     = pageIdx === pages.length - 1;

  /* progress bar */
  const totalSlides = useMemo(
    () => Object.values(chapterPages).reduce((s, arr) => s + arr.length, 0),
    []
  );
  const slidesViewed = useMemo(() => {
    let acc = 0;
    for (let c = 1; c < chapter; c++) acc += chapterPages[c].length;
    return acc + pageIdx;
  }, [chapter, pageIdx]);
  const progress = Math.round(((slidesViewed + 1) / totalSlides) * 100);

  /*────────────────────────────────────────────────────────*/
  /* 4. Chapter-quiz helpers                               */
  /*────────────────────────────────────────────────────────*/
  const startChQuiz = () => {
    const topic = chapterTopicMap[chapter];
    if (!topic) { setAllDone(true); return; }

    setChQuiz(Array.from({ length: 10 },
      () => generateQuizQuestion(topic)));
    setChIdx(0); setChScore(0); setChDone(false); setShowChQuiz(true);
  };

  const handleChAns = (opt) => {
    if (String(opt) === String(chQuiz[chIdx].correctAnswer))
      setChScore((s) => s + 1);

    chIdx < chQuiz.length - 1 ? setChIdx((i) => i + 1) : setChDone(true);
  };

  const finishChQuiz = () => {
    if (chScore >= PASS_CHAPTER) {
      if (chapter < 6) { setChapter((c) => c + 1); setPageIdx(0); }
      else             { setAllDone(true); }
      setShowChQuiz(false);
    } else {
      alert("Below 60 %.  Please review this chapter and try again.");
      startChQuiz();
    }
  };

  /*────────────────────────────────────────────────────────*/
  /* 5. Final-quiz helpers                                 */
  /*────────────────────────────────────────────────────────*/
  const startFinalQuiz = () => {
    const used = new Set();
    const arr  = [];
    while (arr.length < FINAL_COUNT) {
      const q = generateQuizQuestion();           // random pool
      const key = `${q.question}|${q.correctAnswer}`;
      if (!used.has(key)) { used.add(key); arr.push(q); }
    }
    setFinalQs(arr);
    setFIdx(0); setFScore(0); setFDone(false); setShowFinal(true);
    tokenGiven.current = false;
  };

  const handleFinalAns = (opt) => {
    if (String(opt) === String(finalQs[fIdx].correctAnswer))
      setFScore((s) => s + 1);

    fIdx < finalQs.length - 1 ? setFIdx((i) => i + 1) : setFDone(true);
  };

  /* save result + award coin once */
  const tokenGiven = useRef(false);
  useEffect(() => {
    if (!fDone || tokenGiven.current || !auth.currentUser) return;

    const passed = (fScore / finalQs.length) * 100 >= PASS_FINAL_PCT;
    const uid    = auth.currentUser.uid;

    (async () => {
      try {
        await runTransaction(db, async (tx) => {
          const userRef = doc(db, "users", uid);
          const snap    = await tx.get(userRef);
          const data    = snap.data() || {};

          const cp = {
            ...(data.courseProgress || {}),
            [COURSE_ID]: {
              completed: true,
              finalQuizScore: fScore,
              finalQuizTotal: finalQs.length,
              lastCompleted: serverTimestamp()
            }
          };

          const update = { courseProgress: cp };
          if (passed) update.coins = (data.coins || 0) + 1;

          tx.set(userRef, update, { merge: true });
        });

        if (passed) onAwardTokens?.(1);
      } catch (err) {
        console.error("❌  Saving Grade-8 progress failed:", err);
      } finally {
        tokenGiven.current = true;
      }
    })();
  }, [fDone, fScore, finalQs.length, onAwardTokens]);

  /*────────────────────────────────────────────────────────*/
  /* 6.  Reusable Question view                            */
  /*────────────────────────────────────────────────────────*/
  const QuestionCard = ({ q, idx, total, score, answerFn }) => (
    <div className="quick-quiz-story-mode">
      <div className="quiz-top-panel">
        <img src="/images/Teacher.png" alt="Teacher" className="quiz-character-img" />
        <div className="quiz-question-box">
          <p className="quiz-question-text">
            Q {idx + 1}/{total}: {q.question}
          </p>
          {q.visual}
        </div>
      </div>

      <div className="quiz-bottom-panel">
        <div className="quiz-answer-box-left">
          {q.options.map((o, i) => (
            <button key={i} className="quiz-answer-btn" onClick={() => answerFn(o)}>
              {o}
            </button>
          ))}
          <p className="quiz-score-display">Score: {score}</p>

          <button className="button1" onClick={() => setShowCalc((p) => !p)}>
            {showCalc ? "Hide Calculator" : "Show Calculator"}
          </button>
          <button className="button1" onClick={() => setShowPad((p) => !p)}>
            {showPad ? "Hide Writing Pad" : "Show Writing Pad"}
          </button>
          <button className="button1" onClick={() => setShowPlotter((p) => !p)}>
            {showPlotter ? "Hide Plotter" : "Show Plotter"}
          </button>

          {showCalc && <Calculator />}
          {showPad  && <WritingPad />}
          {showPlotter && <PlottingPad widthCells={10} heightCells={10} cellSize={30} />}
        </div>

        <img
          src={user?.avatar || defaultProfile}
          alt="Student"
          className="quiz-character-img-student"
        />
      </div>
    </div>
  );

  /*────────────────────────────────────────────────────────*/
  /* 7.  Render                                            */
  /*────────────────────────────────────────────────────────*/
  return (
    <div className="grade8-math-page">
      <div className="grade8-math-page-scale">

        {/* Progress bar */}
        <div className="progress-wrapper">
          <div className="progress-bar-bg">
            <div className="progress-bar-fg" style={{ width: `${progress}%` }} />
          </div>
          <p>{progress}% complete</p>
        </div>

        {/* Jump menu if finished before */}
        {hasCompletedBefore && !showFinal && !showChQuiz && (
          <div className="jump-ui">
            <p>You previously finished Grade 8 — jump anywhere or retake the final:</p>
            {[1,2,3,4,5].map((c) => (
              <button key={c} onClick={() => { setChapter(c); setPageIdx(0); }}>
                Chapter {c}
              </button>
            ))}
            <button onClick={startFinalQuiz}>Final Quiz</button>
          </div>
        )}

        {/* Lesson or chapter-quiz */}
        {!showFinal && (
          !showChQuiz ? (
            <>
              <h2 className="lesson-page-title">{currentPage.title}</h2>
              <div className="lesson-page-content">{currentPage.body}</div>

              <div className="lesson-page-navigation">
                {pageIdx > 0 && <button className="lesson-nav-btn" onClick={() => setPageIdx((p) => p-1)}>◀ Prev</button>}
                {pageIdx < pages.length-1 && <button className="lesson-nav-btn" onClick={() => setPageIdx((p) => p+1)}>Next ▶</button>}
              </div>

              {isLastPage && <button className="lesson-nav-btn" onClick={startChQuiz}>Take Chapter {chapter} Quiz</button>}
              <br/><Link to="/math" className="grade8-back">← Back to Math</Link>
            </>
          ) : (
            !chDone
              ? <QuestionCard q={chQuiz[chIdx]} idx={chIdx} total={chQuiz.length} score={chScore} answerFn={handleChAns}/>
              : <>
                  <h3>Chapter {chapter} Quiz Finished</h3>
                  <p>You scored {chScore} / {chQuiz.length}</p>
                  <button className="quiz-result-btn" onClick={finishChQuiz}>Continue</button>
                </>
          )
        )}

        {/* Final quiz */}
        {allDone && !showFinal && (
          <div style={{ textAlign:"center" }}>
            <h3>All Chapters Complete!</h3>
            <button className="lesson-nav-btn" onClick={startFinalQuiz}>Start Final Quiz</button>
          </div>
        )}

        {showFinal && (
          !fDone
            ? <QuestionCard q={finalQs[fIdx]} idx={fIdx} total={finalQs.length} score={fScore} answerFn={handleFinalAns}/>
            : <>
                <h3>Final Quiz Finished!</h3>
                <p>You scored {fScore} / {finalQs.length}</p>
                <button className="quiz-result-btn" onClick={() => setShowFinal(false)}>Return to Lesson</button>
              </>
        )}
      </div>
    </div>
  );
}
